import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import {
  ALIGN_CENTER,
  BG_CELL_TITLE,
  BORDER_CELL,
  FONT_TITLE,
} from '../../../../data/excelJsData';
import { construirColumnas, construirDatosExportacion } from './columnasExcel';

const ANCHO_MINIMO_COLUMNA = 10;
const RELLENO_TEXTO_COLUMNA = 2;

/**
 * Calcula el ancho óptimo de una columna según su contenido más largo.
 * @param {string} valorCelda - Contenido de la celda actual.
 * @param {string} valorHeader - Texto del encabezado.
 * @param {number} anchoActual - Ancho actual de la columna.
 * @returns {number} Ancho ajustado.
 */
const calcularAnchoColumna = (valorCelda, valorHeader, anchoActual) => {
  const longitudMaxima = Math.max(valorCelda.length, valorHeader.length);
  const anchoCalculado = Math.max(
    longitudMaxima + RELLENO_TEXTO_COLUMNA,
    ANCHO_MINIMO_COLUMNA
  );
  return anchoCalculado > anchoActual ? anchoCalculado : anchoActual;
};

/**
 * Crea la hoja de cálculo y configura las columnas.
 * @param {ExcelJS.Workbook} workbook - Libro de trabajo.
 * @param {Array<Object>} columnas - Definición de columnas.
 * @returns {ExcelJS.Worksheet} Hoja configurada.
 */
const crearHoja = (workbook, columnas) => {
  const ws = workbook.addWorksheet('Empleados');
  ws.columns = columnas.map(({ key, width }) => ({ key, width }));
  return ws;
};

/**
 * Aplica el estilo de la fila de título (fusión, fuente, fondo, borde).
 * @param {ExcelJS.Worksheet} ws - Hoja de cálculo.
 * @param {number} totalColumnas - Número total de columnas.
 */
const aplicarEstiloTitulo = (ws, totalColumnas) => {
  ws.mergeCells(1, 1, 1, totalColumnas);
  const celda = ws.getCell(1, 1);
  celda.value = 'INFORMACIÓN EMPLEADOS ACTIVOS';
  celda.font = FONT_TITLE;
  celda.alignment = ALIGN_CENTER;
  celda.fill = BG_CELL_TITLE;
  celda.border = BORDER_CELL;
  ws.getRow(1).height = 30;
};

/**
 * Aplica el estilo de la fila de encabezados.
 * @param {ExcelJS.Worksheet} ws - Hoja de cálculo.
 * @param {Array<Object>} columnas - Definición de columnas.
 */
const aplicarEstiloEncabezado = (ws, columnas) => {
  columnas.forEach(({ header }, indice) => {
    const celda = ws.getCell(2, indice + 1);
    celda.value = header;
    celda.font = FONT_TITLE;
    celda.fill = BG_CELL_TITLE;
    celda.alignment = ALIGN_CENTER;
    celda.border = BORDER_CELL;
  });

  ws.getRow(2).height = 25;
};

/**
 * Aplica bordes, alineación y ajusta el ancho de columnas al contenido.
 * @param {ExcelJS.Worksheet} ws - Hoja de cálculo.
 * @param {Array<Object>} columnas - Definición de columnas originales.
 * @returns {Array<Object>} Nuevas columnas con anchos ajustados (sin mutar las originales).
 */
const COLUMNAS_CENTRADAS = ['NumeroDocumento', 'Bloqueado'];

const aplicarBordesYAnchoColumnas = (ws, columnas) => {
  const totalColumnas = ws.columnCount;
  const anchosAjustados = columnas.map(col => ({ ...col }));

  for (let numFila = 3; numFila <= ws.rowCount; numFila++) {
    for (let numCol = 1; numCol <= totalColumnas; numCol++) {
      const celda = ws.getCell(numFila, numCol);
      celda.border = BORDER_CELL;

      const columna = anchosAjustados[numCol - 1];
      if (COLUMNAS_CENTRADAS.includes(columna?.key)) {
        celda.alignment = ALIGN_CENTER;
      }

      const valorCelda = celda.value !== null ? String(celda.value) : '';

      const valorHeader = columna?.header || '';
      const ancho = calcularAnchoColumna(
        valorCelda,
        valorHeader,
        columna.width
      );

      if (ancho > columna.width) {
        anchosAjustados[numCol - 1] = { ...columna, width: ancho };
        ws.getColumn(numCol).width = ancho;
      }
    }
  }

  return anchosAjustados;
};

/**
 * Configura el filtro automático y el formato de moneda en la columna de salario.
 * @param {ExcelJS.Worksheet} ws - Hoja de cálculo.
 * @param {Array<Object>} columnas - Definición de columnas.
 * @param {number} totalFilas - Total de filas de datos.
 */
const configurarFiltroYFormatos = (ws, columnas, totalFilas) => {
  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: totalFilas + 2, column: columnas.length },
  };

  const indiceSalario = columnas.findIndex(col => col.key === 'Salario');
  if (indiceSalario >= 0) {
    ws.getColumn(indiceSalario + 1).numFmt = '$#,##0';
  }
};

/**
 * Escribe el buffer del workbook y descarga el archivo Excel.
 * @param {ExcelJS.Workbook} workbook - Libro de trabajo.
 * @param {string} nombreArchivo - Nombre del archivo a descargar.
 */
const descargarArchivo = async (workbook, nombreArchivo) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, nombreArchivo);
};

/**
 * Descarga un archivo Excel con el listado de empleados.
 * @param {Array<Object>} empleados - Lista de empleados a exportar.
 * @param {Object|null} convenio - Convenio activo (opcional).
 */
export const descargarExcelEmpleados = async (empleados, convenio) => {
  const columnasOriginales = construirColumnas(convenio);
  const workbook = new ExcelJS.Workbook();
  const ws = crearHoja(workbook, columnasOriginales);

  aplicarEstiloTitulo(ws, columnasOriginales.length);
  aplicarEstiloEncabezado(ws, columnasOriginales);

  const datos = construirDatosExportacion(empleados, convenio);
  datos.forEach(fila => ws.addRow(fila));

  const columnasConAnchos = aplicarBordesYAnchoColumnas(ws, columnasOriginales);
  configurarFiltroYFormatos(ws, columnasConAnchos, datos.length);

  await descargarArchivo(workbook, 'EmpleadosNomina.xlsx');
};
